import { UnityContext } from "react-unity-webgl"
import { Direction } from "../types/direction.type"
import { OutlineColor, OUTLINE_COLOR_RED } from '../types/outline-color.type';
import { VisualizationElement } from '../interfaces/visualization-element.interface'
import { Vector3 } from '../interfaces/vector3.interface'
import { Window } from '../interfaces/window.interface';
import { AppContext } from '../interfaces/app-context.interface';
import { VISUALIZATION_TYPE_FLOAT, VISUALIZATION_TYPE_SCREEN } from '../types/visualization-type.type';
import { ContextUtils } from './ContextUtils';
import { ELEMENT_TYPE_SCREEN } from '../types/element-type.type';
import {Vector2} from "../interfaces/vector2.interface";
import {InteractionElement} from "../interfaces/interaction-element.interface";
import { ModelExport } from './ModelExport'

/**
 * declaring global window variables to store uploaded models
 */
declare var window: Window

// Types for the constructor params
type onClickFunction = (clickedElement: string, clickedPlane: Vector3) => void
type onProgressFunction = (progress: number) => void
type stateChangedFunction = (newStateName: string) => void

export class UnityWrapper {

    unityContent: UnityContext  // UnityContent object
    uploadingStarted: () => void     // Method that gets called when starting to upload a model
    uploadingFinished: () => void      // Method that gets called when uploading a model finished
    model: File | undefined

    /**
     * Constructor initializes the unityContent and its methods
     */
    constructor(onClick: onClickFunction, onProgress: onProgressFunction, uploadingStarted: () => void, uploadingFinished: () => void, onStateChanged: stateChangedFunction) {

        this.unityContent = new UnityContext({
          loaderUrl: "Build/WebGL.loader.js",
          dataUrl: "Build/WebGL.data",
          frameworkUrl: "Build/WebGL.framework.js",
          codeUrl: "Build/WebGL.wasm",
        })

        this.unityContent.on("objectClicked", (clickString: string) => {
            const clickObject = JSON.parse(clickString)
            const clickedPlane: Vector3 = { x: clickObject.planeX, y: clickObject.planeY, z: clickObject.planeZ }
            onClick(clickObject.element, clickedPlane)
        })

        this.unityContent.on("stateChanged", (stateName: string) => {
            onStateChanged(stateName)
        })

        this.unityContent.on("progress", (progress: number) => {
            onProgress(progress)
        })

        this.uploadingStarted = uploadingStarted
        this.uploadingFinished = uploadingFinished
    }

    /**
     * Removes all visualization from the current WebGL and displays a certain context
     * @param context       Context that should be displayed
     * @param situationID   Situation that should be visualized (default: current situation)
     */
     updateVisualization(context: AppContext, situationID: number | undefined = undefined) {
        this.removeAllVisualEffects()
        situationID = situationID !== undefined ? situationID : context.applicationState.currentSituationID
        const situation = context.states.find(situation => situation.id === situationID)
        if (!!situation && !!situation.Conditions) {
            for (const condition of situation.Conditions) {
                if (condition.Type === VISUALIZATION_TYPE_FLOAT) {
                    // Add light effects
                    const color = ContextUtils.getLightEmissionColor(condition.VisualizationElement, context.visualizationElements)
                    if (!!color) {
                       this.setLightEffect(condition.VisualizationElement, color.r, color.g, color.b, condition.Value || 0.0)
                    }
                } else if (condition.Type === VISUALIZATION_TYPE_SCREEN) {
                    // Add screen effects
                    const visualizationElement = context.visualizationElements.find(
                        (visualizationElement: VisualizationElement) => (
                            visualizationElement.Name === condition.VisualizationElement && visualizationElement.Type === ELEMENT_TYPE_SCREEN
                        )
                    )
                    if (!!visualizationElement && !!condition.File) {
                        this.addScreenEffect(visualizationElement, condition.File)
                    }
                }
            }
        }
        // Restore outline of selected element
        if (!context.applicationState.isDemoMode) {
            this.outlineElement(context.applicationState.selectedElement, OUTLINE_COLOR_RED)
        }
    }

    /**
     * starts and stops the demo mode
     * @param context          the current app context
     * @param demoModeIsActive whether the demo mode is active or not
     */
    setDemoMode(context: AppContext, demoModeIsActive: boolean) {
        if (demoModeIsActive) {
            console.log("removeAllVisualEffects: " + demoModeIsActive);
            this.removeAllVisualEffects();
            const modelExport = new ModelExport(context);
            console.log("creating model export");
            modelExport.getSpecsZipAsBase64().then((zipContent) => {
                var chunks = this.chunkString(zipContent, 256 * 1024);
                this.unityContent.send(
                  "JavascriptApi",
                  "InitDemoMode",
                  chunks.length.toString());

                chunks.forEach((chunk, idx) => {
                  this.unityContent.send(
                    "JavascriptApi",
                    "ReceiveDemoModeChunk",
                    JSON.stringify({ index: idx, data: chunk })
                  );
                });

                this.unityContent.send(
                  "JavascriptApi",
                  "FinalizeUploadAndStartDemoMode"
                )
            });
        }
        else {
          this.unityContent.send(
            "JavascriptApi",
            "StopDemoMode"
          )
          this.updateVisualization(context);
        }
        
    }

    chunkString(str: string, chunkSize = 64 * 1024) {
        const chunks = [];
        for (let i = 0; i < str.length; i += chunkSize) {
          chunks.push(str.substring(i, i + chunkSize));
        }
        return chunks;
    }      

    /**
     * Uploads a 3D model into the scene
     * @param filename name of the 3D model file
     * @param contents 3D Model file contents
     */
    uploadModel(filename: string, contents: Blob) {
        this.uploadingStarted()
        var reader = new FileReader();

        reader.onload = (event) => {
            console.log("file loaded");

            if (!event.target) {
                return
            }

            if (!!event.target && event.target.result && typeof event.target.result === 'string') {
                const index = event.target?.result?.indexOf(",");
                const modelBase64 = event.target?.result?.substring(index + 1);
                const chunkSize = 10000;

                this.unityContent.send("JavascriptApi", "StartModelUpload", filename);

                for (var i = 0; i < modelBase64.length; i = i + chunkSize) {
                  var chunk = modelBase64.substring(i, Math.min(i + chunkSize, modelBase64.length));
                  this.unityContent.send("JavascriptApi", "HandleModelDataChunk", chunk)
                }

                console.log("finalizing model upload");
                this.unityContent.send("JavascriptApi", "FinishModelUpload");
            }

            this.uploadingFinished()
        }

        this.model = new File([contents], filename);
        
        window.triLibFiles = [];
        reader.readAsDataURL(contents);
    }

    /**
     * Starts moving the camera inside WebGL
     */
    startCameraMovement(direction: Direction) {
        this.unityContent.send(
            "JavascriptApi",
            "StartCameraMovement",
            direction
        )
    }


    /**
     * Stops camera movement inside WebGL
     */
    stopCameraMovement(direction: Direction) {
        this.unityContent.send(
            "JavascriptApi",
            "StopCameraMovement",
            direction
        )
    }

    /**
     * Outlines an element inside WebGL
     */
    outlineElement(elementName: string, colorName: OutlineColor) {
        this.unityContent.send(
            "JavascriptApi",
            "SetOutline",
            JSON.stringify({ element: elementName, color: colorName })
        )
    }

    /**
     * Removes outline of an element 
     */
    removeOutline(elementName: string) {
        this.unityContent.send(
            "JavascriptApi",
            "RemoveOutline",
            elementName
        )
    }

    /**
     * Changes the light effect of an element
     * colors and alpha are from 0 to 1
     */
    setLightEffect(elementName: string, red: number, green: number, blue: number, alpha: number) {
        this.unityContent.send(
            "JavascriptApi",
            "SetLightColor",
            JSON.stringify({ element: elementName, red: red, blue: blue, green: green, alpha: alpha })
        )
    }

    /**
     * Removes all light effects from the current visualization
     */
    removeAllLightEffects() {
        this.unityContent.send(
            "JavascriptApi",
            "RemoveAllLights",
            undefined
        )
    }

    /**
     * Removes the light effect of a certain element
     */
    removeLightEffect(elementName: string) {
        this.unityContent.send(
            "JavascriptApi",
            "RemoveLight",
            elementName
        )
    }

    /**
     * Removes all currently active visual effects
     */
    removeAllVisualEffects() {
        this.unityContent.send(
            "JavascriptApi",
            "RemoveAllVisualEffects",
            ""
        )
    }

    /**
     * Activates a hover effect for the planes of an element 
     */
    activatePlaneHoverEffect(elementName: string) {
        this.unityContent.send(
            "JavascriptApi",
            "activatePlaneHoverEffect",
            elementName
        )
    }

    /**
     * Deactivates plane hover effects
     */
    deActivatePlaneHoverEffect() {
        this.unityContent.send(
            "JavascriptApi",
            "deactivatePlaneHoverEffect",
            ""
        )
    }

    /**
     * Adds a screen effect to an element
     * @param visualizationElement  Definition of the screen element
     * @param imageFile             File that should be displayed
     */
    addScreenEffect(visualizationElement: VisualizationElement, imageFile: File) {
        var reader: FileReader = new FileReader()
        reader.onload = (event) => {
            if (!!event.target && event.target.result && typeof event.target.result === 'string') {
                const index = event.target?.result?.indexOf(",");
                const params = {
                    element: visualizationElement.Name,
                    imageBase64: event.target?.result?.substring(index + 1),       // cut off data type
                    planeX: visualizationElement.Plane?.x,
                    planeY: visualizationElement.Plane?.y,
                    planeZ: visualizationElement.Plane?.z
                }
                this.unityContent.send(
                    "JavascriptApi",
                    "DisplayImage",
                    JSON.stringify(params)
                )
            }
        }
        reader.readAsDataURL(imageFile)
    }

    /**
     * Removes the screen effect of an element
     * @param element   Name of the element
     */
    removeScreenEffect(element: string) {
        this.unityContent.send(
            "JavascriptApi",
            "RemoveScreenEffect",
            element
        )
    }

    /**
     * Adds a screen element to an element
     * @param interactionElement  Interaction element to attach a touchscreen on
     */
    addTouchscreenElement(interactionElement: InteractionElement) {
        const params = {
            element: interactionElement.Name,
            planeX: interactionElement.Plane?.x,
            planeY: interactionElement.Plane?.y,
            planeZ: interactionElement.Plane?.z
        }
        this.unityContent.send(
            "JavascriptApi",
            "AddTouchscreen",
            JSON.stringify(params)
        )
    }

    /**
     * Removes a touchscreen from a GameObject
     * @param element   Name of the element to remove the touchscreen from
     */
    removeTouchscreenElement(element: string) {
        this.unityContent.send(
            "JavascriptApi",
            "RemoveTouchscreen",
            element
        )
    }

    /**
     * Switches the camera behaviour inside WebGL
     */
    switchCameraBehaviour() {
        this.unityContent.send(
            "JavascriptApi",
            "SwitchCameraBehaviour"
        )
    }
}